import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faEye,
  faPenToSquare,
} from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import type { Artifact } from "@/services/api/agentic-v2";
import { Button } from "@/components/ui/button/Button";
import {
  resolvePlanArtifactActions,
  type PlanArtifactActionLabels,
  type PlanArtifactResolvedAction,
  type PlanArtifactRole,
} from "./resolvePlanArtifactActions";

export interface PlanArtifactActionsProps {
  artifact?: Artifact;
  role: PlanArtifactRole;
  groupLabel: string;
  generateLabel: string;
  actionLabels: PlanArtifactActionLabels;
  disabled: boolean;
  onGenerate: () => void;
  onOpenDocument: (artifact: Artifact) => void;
  onOpenDocumentEdit: (artifact: Artifact) => void;
  onOpenReportBuilder: (artifact: Artifact) => void;
  testId: string;
  /** Inline toolbar — no stacked group label (client Plans tab). */
  compact?: boolean;
  /** Single-row toolbar for expanded plan rows — no group labels. */
  inline?: boolean;
}

/** Grouped plan-row artefact actions (LOA or cheat sheet). */
export function PlanArtifactActions({
  artifact,
  role,
  groupLabel,
  generateLabel,
  actionLabels,
  disabled,
  onGenerate,
  onOpenDocument,
  onOpenDocumentEdit,
  onOpenReportBuilder,
  testId,
  compact = false,
  inline = false,
}: PlanArtifactActionsProps) {
  const toolbar = inline || compact;

  if (!artifact) {
    return (
      <Button
        outline
        size="sm"
        onClick={onGenerate}
        disabled={disabled}
        data-testid={testId}
      >
        {generateLabel}
      </Button>
    );
  }

  const actions = resolvePlanArtifactActions(artifact, actionLabels, role);
  const controls = (
    <div
      className={clsx(
        "flex flex-wrap items-center",
        inline ? "gap-1.5" : "gap-1.5 justify-end",
      )}
    >
      {actions.map((action, index) => (
        <PlanActionControl
          key={`${action.kind}-${index}`}
          action={action}
          artifact={artifact}
          inline={inline}
          compact={compact}
          onOpenDocument={onOpenDocument}
          onOpenDocumentEdit={onOpenDocumentEdit}
          onOpenReportBuilder={onOpenReportBuilder}
        />
      ))}
    </div>
  );

  if (toolbar) {
    return <div data-testid={testId}>{controls}</div>;
  }

  return (
    <div className="flex flex-col items-end gap-1" data-testid={testId}>
      <span className="text-[9px] font-medium uppercase tracking-wide text-slate-500">
        {groupLabel}
      </span>
      {controls}
    </div>
  );
}

function PlanActionControl({
  action,
  artifact,
  inline,
  compact,
  onOpenDocument,
  onOpenDocumentEdit,
  onOpenReportBuilder,
}: {
  action: PlanArtifactResolvedAction;
  artifact: Artifact;
  inline?: boolean;
  compact?: boolean;
  onOpenDocument: (artifact: Artifact) => void;
  onOpenDocumentEdit: (artifact: Artifact) => void;
  onOpenReportBuilder: (artifact: Artifact) => void;
}) {
  if (action.kind === "edit") {
    return (
      <PlanActionButton
        icon={faPenToSquare}
        label={action.label}
        inline={inline}
        compact={compact}
        title={`Open ${action.label.toLowerCase()} in Report Builder`}
        onClick={() => onOpenReportBuilder(artifact)}
      />
    );
  }

  if (action.kind === "editDocument") {
    return (
      <PlanActionButton
        icon={faPenToSquare}
        label={action.label}
        inline={inline}
        compact={compact}
        title={`Open ${action.label.toLowerCase()} in the artefact editor`}
        onClick={() => onOpenDocumentEdit(artifact)}
      />
    );
  }

  if (action.href) {
    const icon =
      action.kind === "preview" ? faArrowUpRightFromSquare : faEye;
    return (
      <PlanActionButton
        icon={icon}
        label={action.label}
        inline={inline}
        compact={compact}
        href={action.href}
        title={`Open ${action.label.toLowerCase()}`}
      />
    );
  }

  return (
    <PlanActionButton
      icon={faEye}
      label={action.label}
      inline={inline}
      compact={compact}
      title={`Open ${action.label.toLowerCase()}`}
      onClick={() => onOpenDocument(artifact)}
    />
  );
}

function PlanActionButton({
  icon,
  label,
  onClick,
  href,
  inline,
  compact,
  title,
}: {
  icon: typeof faEye;
  label: string;
  onClick?: () => void;
  href?: string;
  inline?: boolean;
  compact?: boolean;
  title?: string;
}) {
  if (inline) {
    if (href) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title={title ?? label}
          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium text-slate-400 transition-colors hover:bg-white/[0.06] hover:text-slate-200"
        >
          <FontAwesomeIcon icon={icon} className="text-[10px]" />
          {label}
        </a>
      );
    }

    return (
      <Button
        plain
        size="sm"
        type="button"
        onClick={onClick}
        title={title ?? label}
      >
        <FontAwesomeIcon icon={icon} className="text-[10px]" />
        {label}
      </Button>
    );
  }

  const className = clsx(
    "inline-flex items-center gap-1.5 rounded-lg font-medium transition-colors shrink-0",
    compact ? "px-2.5 py-1 text-xs" : "px-2 py-0.5 text-[10px]",
    "text-slate-400 hover:bg-white/[0.06] hover:text-slate-200",
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={title ?? label}
        className={className}
      >
        <FontAwesomeIcon icon={icon} className="text-[10px]" />
        {label}
      </a>
    );
  }

  return (
    <Button
      plain
      size="unstyled"
      type="button"
      onClick={onClick}
      title={title ?? label}
      className={className}
    >
      <FontAwesomeIcon icon={icon} className="text-[10px]" />
      {label}
    </Button>
  );
}
